/** RFC 9457 problem details as sent by the Go API. */
export interface Problem {
  code?: string;
  detail?: string;
  details?: unknown;
  instance?: string;
  requestId?: string;
  status: number;
  title: string;
  type: string;
}

const TYPE_PREFIX = "urn:problem:rkmeeting:";

/** German messages by problem `code`, falling back to the type slug. */
const MESSAGES: Record<string, string> = {
  action_item_not_found: "Diese Aufgabe gibt es nicht (mehr).",
  already_owner: "Diese Person ist bereits Eigentümer:in des Meetings.",
  append_requires_transcript:
    "Eine Aufnahme kann erst ergänzt werden, wenn das Transkript fertig ist.",
  approval_four_eyes:
    "Wer das Protokoll eingereicht hat, darf es nicht selbst freigeben (Vier-Augen-Prinzip).",
  approval_not_allowed:
    "Freigeben oder zurückweisen darf nur der Vorsitz – ohne Vorsitz mit Konto die Eigentümer:in – oder die Administration.",
  audio_not_available:
    "Für dieses Meeting gibt es (noch) kein Audio – oder es wurde nach Ablauf der Aufbewahrungsfrist gelöscht.",
  "bad-request": "Die Anfrage ist ungültig.",
  chunk_conflict:
    "Ein Teil der Aufnahme passt nicht zum bereits gespeicherten Stand.",
  circulation_approver_required:
    "Wer das Protokoll freigibt, bleibt im Umlauf und kann nicht entfernt werden.",
  comment_required: "Bitte geben Sie einen Kommentar bzw. eine Begründung an.",
  comment_too_long: "Der Kommentar ist zu lang (höchstens 2000 Zeichen).",
  conflict: "Das wurde inzwischen geändert. Bitte neu laden.",
  consent_required:
    "Bitte bestätigen Sie, dass alle Anwesenden der Aufzeichnung zugestimmt haben.",
  consent_text_outdated:
    "Der Einwilligungstext wurde inzwischen aktualisiert. Bitte lesen Sie die neue Fassung und willigen Sie erneut ein.",
  cross_origin_rejected: "Anfrage von fremder Herkunft abgelehnt.",
  decision_not_found: "Diesen Beschluss gibt es nicht (mehr).",
  duplicate_participant: "Diese Person steht bereits auf der Teilnehmerliste.",
  evidence_required:
    "Bitte geben Sie an, wo die unterschriebene Einwilligung abgelegt ist.",
  evidence_too_long: "Die Nachweisnotiz ist zu lang (höchstens 1000 Zeichen).",
  feedback_comment_required: "Bitte schreiben Sie Ihre Anmerkung dazu.",
  forbidden: "Dafür fehlt Ihnen die Berechtigung.",
  internal: "Es ist ein Fehler aufgetreten. Bitte später erneut versuchen.",
  invalid_action_item: "Die Angaben zur Aufgabe sind ungültig.",
  invalid_assignee:
    "Diese Person kann nicht als verantwortlich eingetragen werden.",
  invalid_assignment: "Bitte wählen Sie genau eine Person für den Sprecher.",
  invalid_body: "Die gesendeten Daten sind ungültig.",
  invalid_cursor: "Die Liste hat sich geändert. Bitte neu laden.",
  invalid_decision: "Die Angaben zum Beschluss sind ungültig.",
  invalid_expiry:
    "Das Ablaufdatum ist ungültig. Es muss in der Zukunft liegen.",
  invalid_if_match: "Die Änderung bezog sich auf keinen gültigen Stand.",
  invalid_limit: "Die Seitengröße ist ungültig.",
  invalid_meeting: "Die Angaben zum Meeting sind ungültig.",
  invalid_mode: "Die Art der Aufnahme ist ungültig.",
  invalid_name: "Bitte geben Sie einen Namen mit höchstens 100 Zeichen an.",
  invalid_participant: "Die Angaben zu den Teilnehmenden sind ungültig.",
  invalid_person: "Die Angaben zur Person sind ungültig.",
  invalid_protocol: "Das Protokoll ist ungültig oder zu lang.",
  invalid_query: "Die Such- oder Filterangaben sind ungültig.",
  invalid_scopes: "Bitte wählen Sie gültige Rechte für den Zugang.",
  invalid_segment_text:
    "Bitte geben Sie einen Text mit höchstens 10.000 Zeichen ein.",
  invalid_share_level: "Bitte wählen Sie „Lesen“ oder „Bearbeiten“.",
  invalid_share_target: "Bitte wählen Sie genau eine Person oder eine Gruppe.",
  invalid_status: "Der gewählte Status ist ungültig.",
  invalid_trim:
    "Dieser Bereich ist ungültig: Das Ende muss nach dem Anfang liegen und es muss mindestens eine Sekunde übrig bleiben.",
  invalid_verdict: "Bitte wählen Sie „Einverstanden“ oder „Anmerkung“.",
  invalid_version: "Diese Protokollversion ist ungültig.",
  last_part:
    "Der einzige Teil kann nicht gelöscht werden. Löschen Sie stattdessen das ganze Meeting.",
  llm_auth_failed:
    "Der KI-Dienst hat die Zugangsdaten abgelehnt. Bitte wenden Sie sich an die Administration.",
  llm_bad_request: "Der KI-Dienst hat die Anfrage abgelehnt.",
  llm_billing:
    "Beim KI-Dienst ist kein Guthaben mehr vorhanden. Bitte wenden Sie sich an die Administration.",
  llm_context_exceeded: "Das Transkript ist für das KI-Modell zu lang.",
  llm_failed: "Die KI konnte das Protokoll nicht erstellen.",
  llm_input_too_large: "Das Transkript ist für das KI-Modell zu lang.",
  llm_input_truncated:
    "Das KI-Modell hat das Transkript nur gekürzt verarbeitet. Das Protokoll wurde deshalb nicht übernommen.",
  llm_invalid_output:
    "Die KI hat kein verwertbares Protokoll geliefert. Bitte erneut versuchen.",
  llm_max_tokens:
    "Die Antwort der KI war zu lang und wurde abgeschnitten. Bitte erneut versuchen.",
  llm_not_configured:
    "Für die Protokollerstellung ist kein KI-Dienst eingerichtet. Bitte wenden Sie sich an die Administration.",
  llm_rate_limited:
    "Der KI-Dienst ist gerade ausgelastet. Bitte später erneut versuchen.",
  llm_refusal:
    "Die KI hat die Protokollerstellung für dieses Transkript abgelehnt.",
  llm_unavailable:
    "Der KI-Dienst ist nicht erreichbar. Bitte später erneut versuchen.",
  meeting_access_denied: "Für dieses Meeting fehlt Ihnen die Berechtigung.",
  meeting_busy:
    "Für dieses Meeting gibt es bereits Audio oder es wird gerade verarbeitet.",
  meeting_not_found:
    "Dieses Meeting gibt es nicht (mehr) oder es wurde nicht mit Ihnen geteilt.",
  meeting_version_conflict:
    "Jemand anderes hat das Meeting inzwischen geändert. Bitte neu laden und erneut versuchen.",
  "method-not-allowed": "Diese Aktion ist hier nicht möglich.",
  missing_permission: "Dafür fehlt Ihnen die Berechtigung.",
  no_active_run:
    "Es läuft gerade keine Verarbeitung, die abgebrochen werden kann.",
  no_media: "Für dieses Meeting gibt es noch kein Audio.",
  not_in_circulation:
    "Sie (bzw. diese Person) sind nicht im Umlauf dieses Protokolls.",
  "not-found": "Nicht gefunden.",
  part_not_editable:
    "Dieser Teil kann gerade nicht geändert werden (er wird noch aufgenommen oder verarbeitet). Bitte neu laden.",
  part_not_found: "Diesen Teil der Aufnahme gibt es nicht (mehr).",
  part_not_processable:
    "Dieser Teil kann gerade nicht verarbeitet werden. Bitte neu laden.",
  participant_assigned_to_speaker:
    "Diese Person ist einem Sprecher zugeordnet und kann nicht entfernt werden. Lösen Sie zuerst die Zuordnung im Tab „Sprecher“.",
  pdf_unavailable:
    "Der PDF-Dienst ist gerade nicht erreichbar. Bitte später erneut versuchen.",
  person_edit_forbidden:
    "Diese Person kann nur von der Person, die sie angelegt hat, oder der Administration geändert werden.",
  person_managed_by_directory:
    "Rotkreuz-Konten werden in Authentik gepflegt und können hier nicht geändert werden.",
  person_not_found: "Diese Person gibt es nicht (mehr).",
  "precondition-failed": "Jemand anderes hat das inzwischen geändert.",
  processing_active: "Das Meeting wird bereits verarbeitet.",
  protocol_generation_running:
    "Das Protokoll wird gerade von der KI erstellt. Bitte warten Sie, bis die Erstellung abgeschlossen ist.",
  protocol_locked:
    "Das Protokoll ist in Prüfung oder freigegeben und kann nicht geändert werden. Weisen Sie es zuerst zurück bzw. öffnen Sie es wieder.",
  protocol_not_approved: "Das Protokoll ist nicht freigegeben.",
  protocol_not_draft:
    "Das Protokoll ist bereits eingereicht oder freigegeben. Bitte neu laden.",
  protocol_not_found: "Für dieses Meeting gibt es noch kein Protokoll.",
  protocol_not_in_review:
    "Das Protokoll ist nicht (mehr) in Prüfung. Bitte neu laden.",
  protocol_version_conflict:
    "Jemand anderes hat das Protokoll inzwischen geändert.",
  protocol_version_not_found: "Diese Protokollversion gibt es nicht.",
  recording_abandoned:
    "Diese Aufnahme wurde verworfen oder ist abgelaufen und kann nicht fortgesetzt werden.",
  recording_finalized: "Diese Aufnahme ist bereits abgeschlossen.",
  recording_incomplete:
    "Die Aufnahme ist unvollständig – es fehlen noch Teile. Bitte die Seite geöffnet lassen, bis alles übertragen ist.",
  reopen_not_allowed:
    "Wieder öffnen dürfen nur Eigentümer:in, Vorsitz oder die Administration.",
  same_speaker:
    "Ein Sprecher kann nicht mit sich selbst zusammengeführt werden.",
  segment_not_found: "Diesen Transkript-Abschnitt gibt es nicht (mehr).",
  "service-unavailable": "Der Dienst ist vorübergehend nicht erreichbar.",
  session_required:
    "Zugänge können nur nach Anmeldung im Browser verwaltet werden.",
  share_exists:
    "Das Meeting ist bereits für dieses Ziel freigegeben. Ändern Sie stattdessen die Stufe.",
  share_not_found: "Diese Freigabe gibt es nicht (mehr).",
  share_with_owner: "Der Eigentümer hat bereits vollen Zugriff.",
  share_with_self: "Sie können das Meeting nicht für sich selbst freigeben.",
  speaker_merge_untracked:
    "Diese Zusammenführung stammt aus einer älteren Version und kann nicht rückgängig gemacht werden.",
  speaker_merged:
    "Dieser Sprecher wurde bereits mit einem anderen zusammengeführt. Bitte neu laden.",
  speaker_not_found:
    "Diesen Sprecher gibt es im aktuellen Transkript nicht (mehr).",
  speaker_not_merged:
    "Dieser Sprecher ist nicht (mehr) zusammengeführt. Bitte neu laden.",
  suggestion_not_found: "Dieser Vorschlag ist nicht mehr aktuell.",
  text_version_required:
    "Bitte lesen Sie den Einwilligungstext und bestätigen Sie ihn.",
  token_lifetime_exceeded:
    "Der Zugang darf höchstens so lange gültig sein, wie die Administration erlaubt.",
  token_limit_reached:
    "Sie haben bereits die höchste Zahl aktiver Zugänge. Bitte widerrufen Sie zuerst einen nicht mehr benötigten Zugang.",
  token_not_found: "Diesen Zugang gibt es nicht (mehr).",
  too_little_speech:
    "In der Aufnahme war zu wenig klare Sprache. Bitte nehmen Sie in ruhiger Umgebung erneut auf und lesen Sie den ganzen Text vor.",
  "too-large": "Die Datei ist zu groß.",
  "too-many-requests": "Zu viele Anfragen. Bitte kurz warten.",
  transcript_busy:
    "Ein Teil der Aufnahme wird gerade geändert. Bitte warten Sie, bis die Verarbeitung fertig ist, und laden Sie das Transkript neu.",
  transcript_empty:
    "Das Transkript enthält keinen Text, aus dem ein Protokoll erstellt werden kann.",
  transcript_not_found: "Für dieses Meeting gibt es noch kein Transkript.",
  trim_unchanged: "Der Teil hat bereits genau diese Kürzung.",
  unauthorized: "Bitte melden Sie sich an.",
  unknown_group: "Diese Gruppe gibt es nicht (mehr).",
  unknown_person: "Diese Person gibt es nicht (mehr).",
  unknown_user: "Dieses Konto gibt es nicht oder es ist deaktiviert.",
  unsupported_file_type:
    "Dieser Dateityp wird nicht unterstützt. Bitte eine Audio- oder Videodatei wählen.",
  upload_not_found:
    "Der Upload wurde nicht gefunden. Bitte die Datei erneut hochladen.",
  upload_too_large: "Die Datei ist zu groß.",
  voice_consent_required:
    "Stimmproben können erst nach Ihrer Einwilligung berechnet werden.",
  voice_profiles_disabled:
    "Stimmprofile wurden von der Administration noch nicht freigeschaltet.",
  voice_recording_empty: "Die Aufnahme ist leer. Bitte erneut aufnehmen.",
  voice_recording_limit:
    "Sie haben in der letzten Stunde schon fünf Stimmproben aufgenommen. Bitte versuchen Sie es später erneut.",
  voice_recording_not_found: "Diese Aufnahme gibt es nicht (mehr).",
  voice_recording_too_large:
    "Die Aufnahme ist zu groß. Bitte nehmen Sie höchstens eine Minute auf.",
  voice_sample_not_found: "Diese Stimmprobe gibt es nicht (mehr).",
};

const FALLBACK = MESSAGES.internal as string;

/** Error thrown by apiFetch for every non-2xx response. */
export class ApiError extends Error {
  readonly problem: Problem;

  constructor(problem: Problem) {
    super(problem.detail ?? problem.title);
    this.name = "ApiError";
    this.problem = problem;
  }

  get status(): number {
    return this.problem.status;
  }

  get slug(): string {
    return this.problem.type.startsWith(TYPE_PREFIX)
      ? this.problem.type.slice(TYPE_PREFIX.length)
      : this.problem.type;
  }
}

/** German, user-facing text for any error. */
export function errorMessage(error: unknown): string {
  if (error instanceof ApiError) {
    const byCode = error.problem.code
      ? MESSAGES[error.problem.code]
      : undefined;
    return byCode ?? MESSAGES[error.slug] ?? FALLBACK;
  }
  return FALLBACK;
}

/** German text for a stable code outside a problem (e.g. a job status). */
export function messageForCode(code: string | undefined): string {
  return (code ? MESSAGES[code] : undefined) ?? FALLBACK;
}

export function isProblem(value: unknown): value is Problem {
  return (
    typeof value === "object" &&
    value !== null &&
    "type" in value &&
    "status" in value &&
    "title" in value
  );
}
